import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FileSpreadsheet, FileText, Loader2, RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";
import { AdminPageHero, adminHeroActionClassName } from "../../modules/admin/ui";

type MovementRow = {
  id: number;
  kind: string;
  movementType?: string;
  quantity: number;
  createdAt: string;
  fournisseur: string | null;
  commentaire: string | null;
  resourceCatalog: { code: string; nom: string };
  passage: { id: number } | null;
  client: { id: number; nomEntreprise: string | null; nom: string | null; prenom: string | null } | null;
  tournee: { id: number; numero: string | null } | null;
  equipe: { id: number; nom: string } | null;
  user: { nom: string; prenom: string | null } | null;
};

const MOVEMENT_KIND_OPTIONS = [
  { value: "", label: "Tous les types" },
  { value: "ENTREE_STOCK", label: "Entrée stock" },
  { value: "SORTIE_PASSAGE", label: "Sortie passage" },
  { value: "SORTIE_PREMIERE_INTERVENTION", label: "Sortie 1ère intervention" },
  { value: "DOTATION_CLIENT", label: "Dotation client" },
  { value: "EXIT_MANUAL", label: "Sortie manuelle" },
  { value: "AJUSTEMENT", label: "Ajustement" },
  { value: "RETOUR", label: "Retour" },
  { value: "PERTE", label: "Perte" },
  { value: "MAINTENANCE", label: "Maintenance sortie" },
  { value: "MAINTENANCE_RETURN", label: "Retour maintenance" },
] as const;

function apiErr(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { error?: { message?: string } | string } } };
  const e = ax.response?.data?.error;
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) return String(e.message);
  return fallback;
}

function clientLabel(m: MovementRow): string {
  if (m.client) {
    return (
      m.client.nomEntreprise?.trim() ||
      [m.client.prenom, m.client.nom].filter(Boolean).join(" ").trim() ||
      "—"
    );
  }
  return m.fournisseur ?? "—";
}

const ResourceMovementsPage: React.FC = () => {
  const [rows, setRows] = useState<MovementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exportBusy, setExportBusy] = useState<"excel" | "pdf" | null>(null);
  const [search, setSearch] = useState("");
  const [client, setClient] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [kind, setKind] = useState("");

  const queryParams = useMemo(
    () => ({
      limit: 300,
      search: search.trim() || undefined,
      client: client.trim() || undefined,
      from: from || undefined,
      to: to || undefined,
      kind: kind || undefined,
    }),
    [search, client, from, to, kind]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/resources/movements", { params: queryParams });
      setRows((res.data?.rows ?? []) as MovementRow[]);
      setTotal(Number(res.data?.total ?? 0));
    } catch (err) {
      setRows([]);
      setTotal(0);
      toast.error(apiErr(err, "Impossible de charger les mouvements catalogue."));
    } finally {
      setLoading(false);
    }
  }, [queryParams]);

  useEffect(() => {
    const delay = search || client ? 350 : 0;
    const t = window.setTimeout(() => void load(), delay);
    return () => window.clearTimeout(t);
  }, [load, search, client, from, to, kind]);

  const exportMovements = async (format: "excel" | "pdf") => {
    setExportBusy(format);
    try {
      const res = await api.get("/resources/movements/export", {
        params: { format, ...queryParams, limit: undefined },
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type:
          format === "excel"
            ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            : "application/pdf",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mouvements-stock-${Date.now()}.${format === "excel" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(format === "excel" ? "Export Excel téléchargé." : "Export PDF téléchargé.");
    } catch {
      toast.error("Export impossible pour le moment.");
    } finally {
      setExportBusy(null);
    }
  };

  const hasFilters = Boolean(search.trim() || client.trim() || from || to || kind);

  return (
    <div className="space-y-5 text-slate-100">
      <AdminPageHero
        eyebrow="Terrain"
        title="Mouvements stock (catalogue)"
        subtitle={`Historique agrégé (entrées, sorties passage, ajustements, etc.) — ${total} enregistrement(s)${hasFilters ? " (filtres actifs)" : ""}.`}
        actions={
          <>
            <button
              type="button"
              onClick={() => void load()}
              className={adminHeroActionClassName(false)}
            >
              <RefreshCcw className={`mr-2 inline h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </button>
            <button
              type="button"
              onClick={() => void exportMovements("excel")}
              disabled={exportBusy !== null}
              className={adminHeroActionClassName(false)}
            >
              {exportBusy === "excel" ? (
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <FileSpreadsheet className="mr-2 inline h-4 w-4" aria-hidden />
              )}
              Export Excel
            </button>
            <button
              type="button"
              onClick={() => void exportMovements("pdf")}
              disabled={exportBusy !== null}
              className={adminHeroActionClassName(false)}
            >
              {exportBusy === "pdf" ? (
                <Loader2 className="mr-2 inline h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <FileText className="mr-2 inline h-4 w-4" aria-hidden />
              )}
              Export PDF
            </button>
          </>
        }
      />

      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filtres</p>
          {hasFilters ? (
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setClient("");
                setFrom("");
                setTo("");
                setKind("");
              }}
              className="text-xs text-sky-400 hover:text-sky-300"
            >
              Réinitialiser les filtres
            </button>
          ) : null}
        </div>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Recherche
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Produit, passage, tournée…"
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Client
            <input
              type="search"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              placeholder="Nom client ou fournisseur…"
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder:text-slate-500"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Du
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Au
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Type mouvement
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
            >
              {MOVEMENT_KIND_OPTIONS.map((opt) => (
                <option key={opt.value || "all"} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <p className="flex items-center justify-center gap-2 rounded-2xl border border-slate-800 bg-slate-900/40 py-16 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" /> Chargement…
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/40">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/80 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Type mouvement</th>
                <th className="px-4 py-3">Produit</th>
                <th className="px-4 py-3">Qté</th>
                <th className="px-4 py-3">Client lié</th>
                <th className="px-4 py-3">Passage</th>
                <th className="px-4 py-3">Tournée</th>
                <th className="px-4 py-3">Équipe</th>
                <th className="px-4 py-3">Utilisateur</th>
                <th className="px-4 py-3">Commentaire</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td className="px-4 py-8 text-slate-500" colSpan={10}>
                    Aucun mouvement catalogue pour ces critères.
                  </td>
                </tr>
              ) : (
                rows.map((m) => (
                  <tr key={m.id} className="border-b border-slate-800/80">
                    <td className="whitespace-nowrap px-4 py-3 text-slate-300">
                      {new Date(m.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sky-300">{m.movementType ?? m.kind}</td>
                    <td className="px-4 py-3 text-slate-200">
                      <span className="font-mono text-emerald-200/90">{m.resourceCatalog.code}</span>
                      <span className="block text-xs text-slate-500">{m.resourceCatalog.nom}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-300">{m.quantity}</td>
                    <td className="px-4 py-3 text-slate-400">{clientLabel(m)}</td>
                    <td className="px-4 py-3 text-slate-400">
                      {m.passage ? `#${m.passage.id}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {m.tournee ? m.tournee.numero ?? `#${m.tournee.id}` : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{m.equipe?.nom ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-400">
                      {m.user ? [m.user.prenom, m.user.nom].filter(Boolean).join(" ") : "—"}
                    </td>
                    <td className="max-w-xs px-4 py-3 text-slate-500">{m.commentaire ?? "—"}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ResourceMovementsPage;
